import { useCallback, useEffect, useRef, useState } from 'react';

export function isDocumentPipSupported() {
  return typeof window !== 'undefined' && 'documentPictureInPicture' in window;
}

function copyDocumentStyles(target) {
  const doc = target.document;
  [...document.styleSheets].forEach((sheet) => {
    try {
      const css = [...sheet.cssRules].map((rule) => rule.cssText).join('\n');
      const style = doc.createElement('style');
      style.textContent = css;
      doc.head.appendChild(style);
    } catch {
      if (!sheet.href) return;
      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = sheet.href;
      if (sheet.media?.mediaText) link.media = sheet.media.mediaText;
      doc.head.appendChild(link);
    }
  });
  doc.documentElement.className = document.documentElement.className;
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme) doc.documentElement.setAttribute('data-theme', theme);
  doc.body.className = document.body.className;
  doc.body.style.margin = '0';
  doc.body.style.background = '#020617';
}

/**
 * Chrome Document Picture-in-Picture: an always-on-top window that stays visible
 * on every tab (and over other apps). open() must be called synchronously from a click.
 */
export function useDocumentPip({ title = 'Twinnee recorder' } = {}) {
  const [pipWindow, setPipWindow] = useState(null);
  const windowRef = useRef(null);
  const supported = isDocumentPipSupported();

  const open = useCallback(async ({ width = 380, height = 260 } = {}) => {
    if (!isDocumentPipSupported()) return null;
    const existing = window.documentPictureInPicture.window;
    if (existing) {
      windowRef.current = existing;
      setPipWindow(existing);
      return existing;
    }
    const win = await window.documentPictureInPicture.requestWindow({ width, height });
    copyDocumentStyles(win);
    win.document.title = title;
    win.addEventListener('pagehide', () => {
      if (windowRef.current === win) windowRef.current = null;
      setPipWindow((cur) => (cur === win ? null : cur));
    });
    windowRef.current = win;
    setPipWindow(win);
    return win;
  }, [title]);

  const close = useCallback(() => {
    const win = windowRef.current;
    windowRef.current = null;
    setPipWindow(null);
    try {
      win?.close();
    } catch {
      /* already closed */
    }
  }, []);

  useEffect(() => () => {
    try {
      windowRef.current?.close();
    } catch {
      /* already closed */
    }
  }, []);

  return { supported, pipWindow, open, close };
}
